import React, { useEffect, useState } from 'react'
import Table from 'react-bootstrap/Table'
import Modal from 'react-bootstrap/Modal'
import { useNavigate } from 'react-router-dom'
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'
import { db } from '../firebase'
import MaskedPhone from '../Components/MaskedPhone';

function MediaConsentComp() {
  const navigate = useNavigate()
  const [requests, setRequests] = useState([])
  const [statsByRequest, setStatsByRequest] = useState({})
  const [loading, setLoading] = useState(true)
  const [modalShow, setModalShow] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalStudents, setModalStudents] = useState([])
  const [modalStatus, setModalStatus] = useState('approved')
  const [modalRequestTitle, setModalRequestTitle] = useState('')
  const [mediaModalShow, setMediaModalShow] = useState(false)
  const [mediaModalRequest, setMediaModalRequest] = useState(null)

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const q = query(collection(db, 'MediaConsentRequests'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        const items = []
        snapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() })
        })
        setRequests(items)
      } catch (err) {
        console.error('Failed to load media consent requests', err)
      } finally {
        setLoading(false)
      }
    }

    fetchRequests()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'MediaConsentTokens'))
        const map = {}
        snapshot.forEach((doc) => {
          const data = doc.data()
          const requestId = data.requestId
          if (!requestId) return
          if (!map[requestId]) {
            map[requestId] = { approved: 0, declined: 0, total: 0 }
          }
          map[requestId].total += 1
          if (data.status === 'approved') {
            map[requestId].approved += 1
          } else if (data.status === 'declined') {
            map[requestId].declined += 1
          }
        })
        setStatsByRequest(map)
      } catch (err) {
        console.error('Failed to load media consent stats', err)
      }
    }

    fetchStats()
  }, [])

  const getStatus = (approved, declined, total) => {
    if (declined > 0) return 'Declined'
    if (total > 0 && approved === total) return 'Approved'
    return 'Pending'
  }

  const getStatusClass = (status) => {
    if (status === 'Approved') return 'bg-success'
    if (status === 'Declined') return 'bg-danger'
    return 'bg-warning'
  }

  const getVideoEmbedUrl = (url) => {
    try {
      const parsed = new URL(url)
      const host = parsed.hostname.replace('www.', '')
      if (host === 'youtu.be') {
        const videoId = parsed.pathname.slice(1)
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('youtube.com')) {
        const videoId = parsed.searchParams.get('v')
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('vimeo.com')) {
        const parts = parsed.pathname.split('/').filter(Boolean)
        const videoId = parts[parts.length - 1]
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null
      }
    } catch (err) {
      return null
    }
    return null
  }

  const isDirectVideo = (url) => {
    return /\.(mp4|webm|ogg)(\?|#|$)/i.test(url)
  }

  const openMediaModal = (request) => {
    setMediaModalRequest(request)
    setMediaModalShow(true)
  }

  const renderMediaItems = (request) => {
    if (!request || !request.media || request.media.length === 0) {
      return <div className="text-center">No media attached.</div>
    }

    return (
      <div className="media-consent-media">
        {request.media.map((item) => (
          <div key={item.url} className="media-consent-media-item">
            {item.type && item.type.startsWith('image') && <img src={item.url} alt={item.name || 'Media'} />}
            {item.type && item.type.startsWith('video') && (() => {
              const embedUrl = getVideoEmbedUrl(item.url)
              if (embedUrl) {
                return (
                  <iframe
                    src={embedUrl}
                    title={item.name || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )
              }
              if (isDirectVideo(item.url)) {
                return <video controls src={item.url} />
              }
              return (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                  Open Video
                </a>
              )
            })()}
          </div>
        ))}
      </div>
    )
  }

  const openModal = async (request, status) => {
    setModalShow(true)
    setModalLoading(true)
    setModalStudents([])
    setModalStatus(status)
    setModalRequestTitle(request.title || '')

    try {
      const q = query(
        collection(db, 'MediaConsentTokens'),
        where('requestId', '==', request.id),
        where('status', '==', status)
      )
      const snapshot = await getDocs(q)
      const items = []
      snapshot.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() })
      })
      setModalStudents(items)
    } catch (err) {
      console.error('Failed to load consent responses', err)
    } finally {
      setModalLoading(false)
    }
  }

  return (
    <div className="clientsComp">
      <div className="dataCompTop">
        <h5>
          Media Consent Requests{' '}
          <span className="badge text-bg-success">{requests.length}</span>
        </h5>
        <div className="searchContainer">
          <button onClick={() => navigate('/media-consent/new')}>New Request</button>
        </div>
      </div>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Title</th>
            <th>Created</th>
            <th>Students</th>
            <th>Media</th>
            <th>Approved</th>
            <th>Declined</th>
            <th>Pending</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {!loading && requests.length === 0 && (
            <tr>
              <td colSpan="8" className="text-center">No media consent requests yet</td>
            </tr>
          )}
          {requests.map((request) => {
            const stats = statsByRequest[request.id] || { approved: 0, declined: 0, total: 0 }
            const requestTotal = request.studentCount || (request.studentIds ? request.studentIds.length : 0)
            const total = requestTotal || stats.total
            const approved = stats.approved || 0
            const declined = stats.declined || 0
            const pending = Math.max(0, total - approved - declined)
            const status = getStatus(approved, declined, total)
            const createdAt = request.createdAt && request.createdAt.toDate ? request.createdAt.toDate() : null

            return (
              <tr key={request.id}>
                <td>{request.title}</td>
                <td>{createdAt ? `${createdAt.toDateString()} ${createdAt.toLocaleTimeString()}` : '—'}</td>
                <td>{total}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    type="button"
                    onClick={() => openMediaModal(request)}
                  >
                    View
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    disabled={approved === 0}
                    onClick={() => openModal(request, 'approved')}
                  >
                    {approved}
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-link p-0"
                    type="button"
                    disabled={declined === 0}
                    onClick={() => openModal(request, 'declined')}
                  >
                    {declined}
                  </button>
                </td>
                <td>{pending}</td>
                <td>
                  <span className={`badge ${getStatusClass(status)}`}>{status}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalRequestTitle} - {modalStatus === 'approved' ? 'Approved' : 'Declined'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading && <div className="text-center">Loading responses...</div>}
          {!modalLoading && modalStudents.length === 0 && (
            <div className="text-center">No students found.</div>
          )}
          {!modalLoading && modalStudents.length > 0 && (
            <div className="table-responsive">
              <Table bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Responded At</th>
                  </tr>
                </thead>
                <tbody>
                  {modalStudents.map((student) => {
                    const respondedAt = student.respondedAt && student.respondedAt.toDate
                      ? student.respondedAt.toDate()
                      : null
                    return (
                      <tr key={student.id}>
                        <td>{student.studentName || '—'}</td>
                        <td>
                          <MaskedPhone
                            phoneNumber={student.phoneNumber}
                            studentId={student.studentId || student.userId}
                            studentName={student.studentName}
                          />
                        </td>
                        <td>{respondedAt ? respondedAt.toLocaleString() : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={mediaModalShow} onHide={() => setMediaModalShow(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Media Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mediaModalRequest && (
            <div>
              <h5>{mediaModalRequest.title}</h5>
              <p className="text-muted">{mediaModalRequest.description}</p>
              {renderMediaItems(mediaModalRequest)}
            </div>
          )}
          {!mediaModalRequest && <div className="text-center">No media details available.</div>}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default MediaConsentComp
